import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import {
  buildRequestParams,
  getCountAndSelected,
  getStartEndDate,
  ProgressSeriesLimit,
  validParams,
} from "../ReportUtil";
import slugify from "slugify";
import clsx from "clsx";
import { Transition } from "@headlessui/react";
import { REACT_APP_ADMIN_LINK, useDMQuery } from "../../../utils";
import {
  Standard,
  StandardOption,
  SubStandard,
} from "../../../manager/components/standards/common/types";
import { DeltaMathSelect } from "../../../shared/DeltaMathSelect";
import ProgressSelectStandard from "./ProgressSelectStandard";
import { get, isEqual, toLower } from "lodash";
import BackArrowIcon from "../../../shared/icons/BackArrowIcon";
import { DisplayPerformanceGraphs } from "./ProgressGraph";
import DisplayGlobalPerformanceGraph from "./ProgressGraphByCourse";
import {
  DistrictData,
  ProgressLocalStorageInterface,
  SchoolData,
  SchoolsWithIntegral,
  SectionData,
  TeacherData,
  ProgressByStandardRequestInterface,
  ProgressRequestInterface,
  ProgressData,
} from "../ReportTypes";
import ReportDataScope from "../shared/ReportDataScope";
import LoadingSkeloton from "../../utils/LoadingSkeleton";
import { ProgressFormattedCurrentSelection } from "./ProgressFormatCurrentSelection";
import { ProgressReporWrapper } from "./ProgressReportWrapper";

const scopeTabs = ["Schools", "Teachers", "Sections"] as Array<
  ProgressLocalStorageInterface["scope"]
>;

const StudentPerformanceContainer = ({
  schoolsWithIntegral,
  schoolData,
  districtData,
}: {
  schoolsWithIntegral: SchoolsWithIntegral[];
  schoolData: SchoolData[];
  districtData: DistrictData;
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const {
    yesterday,
    today,
    yearToDate,
    lastYearToDate,
    lastYearEnd,
    currentkey,
    lastKey,
  } = getStartEndDate();

  // References to memoized request parameters
  const prevProgressByStandardlRequestParamsRef =
    useRef<ProgressByStandardRequestInterface | null>(null);
  const prevProgressRequestParamsRef = useRef<ProgressRequestInterface | null>(
    null
  );

  const [stateData, setStateData] =
    useState<ProgressLocalStorageInterface | null>(null);
  const [progressByStandardRequestParams, setProgressByStandardRequestParams] =
    useState<ProgressByStandardRequestInterface>({
      skillCodesToStandards: {},
    });
  const [progressParams, setProgressParams] =
    useState<ProgressRequestInterface>({ series: [], skillcodes: [] });
  const [selectCourseOptions, setSelectCourseOptions] =
    useState<Array<{ key: string; val: string }>>();
  const [selectedStandard, setSelectedStandard] = useState<
    Standard | SubStandard
  >();

  const yearOptions = [
    {
      val: `Year Beginning 8/${yearToDate.getFullYear().toString(10).slice(2)}`,
      key: currentkey,
      startDate: yearToDate,
      endDate: today,
    },
    {
      val: `Year Beginning 8/${lastYearToDate
        .getFullYear()
        .toString(10)
        .slice(2)}`,
      key: lastKey,
      startDate: lastYearToDate,
      endDate: lastYearEnd,
    },
  ];

  // Fetch Teacher data
  const { data: teacherData, isLoading: teacherDataLoading } = useDMQuery({
    path: "/admin_new/data/teacher",
    queryOptions: {
      staleTime: 1000 * 60 * 15,
    },
  });

  // Fetch Section data
  const { data: sectionData, isLoading: sectionDataLoading } = useDMQuery({
    path: "/admin_new/data/sections",
    queryOptions: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 15,
    },
  });

  // Fetch the course data for the selected course
  const { data: courseData, refetch: refetchCourseData } = useDMQuery<Standard>(
    {
      path: `/standards/${stateData?.selectedCourse}`,
      queryOptions: {
        enabled: false,
        staleTime: 1000 * 60 * 15,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
      },
    }
  );

  // Fetch the standards for the user state code (as determined by the school, district, or user profile)
  const { data: standards, refetch: refetchStandards } = useDMQuery<
    StandardOption[]
  >({
    path: `/standards/code/${stateData?.state}`,
    queryOptions: {
      enabled: false,
      staleTime: 1000 * 60 * 15,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  });

  // Fetch the AP standards data, always provide this option regardless of the user state code
  const { data: apStandards } = useDMQuery<StandardOption[]>({
    path: `/standards/code/AP`,
    queryOptions: {
      staleTime: 1000 * 60 * 15,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  });

  // Fetch the progress by standards data
  const {
    data: progressByStandardsData,
    refetch: refetchProgressByStandards,
    isLoading: progressByStandardsLoading,
    error: progressByStandardsError,
  } = useDMQuery({
    cacheKey: [
      `/admin_new/data/progressByStandards`,
      JSON.stringify(progressByStandardRequestParams),
    ],
    path: `/admin_new/data/progressByStandards`,
    requestBody: progressByStandardRequestParams,
    method: "post",
    queryOptions: {
      enabled: false,
      retry: 0,
    },
  });

  // Fetch the progress data
  const {
    data: progressData,
    refetch: refretchProgressData,
    isLoading: progressDataLoading,
    error: progressDataError,
  } = useDMQuery<ProgressData>({
    cacheKey: [`/admin_new/data/progress`, JSON.stringify(progressParams)],
    path: `/admin_new/data/progress`,
    requestBody: progressParams,
    method: "post",
    queryOptions: {
      enabled: false,
      retry: 0,
    },
  });

  // Handle default navigation
  useEffect(() => {
    if (!stateData?.scope) {
      return;
    }

    if (!params.root) {
      navigate(
        `${REACT_APP_ADMIN_LINK}/reports/student-performance/select-scope`
      );
    }
    if (params.root === "select-scope" && !params.page) {
      navigate(
        `${REACT_APP_ADMIN_LINK}/reports/student-performance/select-scope/${stateData.scope.toLowerCase()}`
      );
    }
  }, [params, stateData?.scope]);

  // Initialize stateData from local storage or set default values
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    let raw = JSON.parse(localStorage.getItem("perfPreferences") || "{}") || {};
    if (!stateData) {
      // If the user change reset everything to default
      if (!isEqual(user._id, raw.userId)) {
        raw = {};
      }

      setStateData({
        userId: user?._id || "",
        startDate: raw.startDate ? new Date(raw.startDate) : yearToDate,
        endDate: raw.endDate ? new Date(raw.endDate) : today,
        scope: raw.scope || "Schools",
        selectedSchools: raw.selectedSchools ?? null,
        selectedTeachers: raw.selectedTeachers ?? null,
        selectedSections: raw.selectedSections ?? null,
        assignmentType: raw.assignmentType || "all",
        state: raw.state || user?.schoolinfo?.state || "AP",
        activatedPath: raw.activatedPath || "",
        selectedCourse: raw.selectedCourse || "",
        selectedYear: raw.selectedYear || yearOptions[0].val,
        skillcodes: raw.skillcodes || [],
        axisOption: raw.axisOption || "Fluency",
      });
    }
  }, [yearToDate, yesterday]);

  // Manage local storage for usage preferences
  useEffect(() => {
    if (stateData) {
      localStorage.setItem("perfPreferences", JSON.stringify(stateData));
    }
  }, [stateData]);

  // Refetch /progressByStandard when requestParams change
  useEffect(() => {
    const paramsChanged = !isEqual(
      prevProgressByStandardlRequestParamsRef.current,
      progressByStandardRequestParams
    );

    if (paramsChanged && validParams(progressByStandardRequestParams)) {
      refetchProgressByStandards();
    }

    prevProgressByStandardlRequestParamsRef.current =
      progressByStandardRequestParams;
  }, [progressByStandardRequestParams, refetchProgressByStandards]);

  // Refetch /progress when requestParams change
  useEffect(() => {
    const paramsChanged = !isEqual(
      prevProgressRequestParamsRef.current,
      progressParams
    );

    if (
      paramsChanged &&
      validParams(progressParams) &&
      progressParams.skillcodes &&
      progressParams.skillcodes.length
    ) {
      refretchProgressData();
    }

    prevProgressRequestParamsRef.current = progressParams;
  }, [progressParams, refetchProgressByStandards]);

  // When the scope changes, update the request parameters
  useEffect(() => {
    // Progress By Standard Request Parameters
    const params = buildProgressByStandardRequestParams(
      stateData,
      courseData,
      schoolsWithIntegral,
      teacherData,
      sectionData
    );
    if (params) {
      setProgressByStandardRequestParams(params);
    }

    // Progress Request Parameters
    const progressParams = buildProgressRequestParams(
      stateData,
      schoolsWithIntegral,
      teacherData,
      sectionData
    );
    if (progressParams) {
      setProgressParams(progressParams);
    }
  }, [schoolsWithIntegral, teacherData, sectionData, stateData, courseData]);

  // Manage the user state code
  // Note: Race conditions with setStateData have been observed here, so the functional update form is used
  useEffect(() => {
    if (stateData) {
      if (
        districtData?.address?.state &&
        stateData.state !== districtData.address.state
      ) {
        setStateData((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            state: districtData.address.state,
          };
        });
      } else if (schoolData && schoolData.length > 0) {
        const schoolState = schoolData[0].address?.state;
        if (schoolState && stateData.state !== schoolState) {
          setStateData((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              state: schoolState,
            };
          });
        }
      }
    }
  }, [schoolData, districtData, stateData, refetchStandards]);

  // Re-fetch standards when state code changes
  useEffect(() => {
    if (stateData?.state) {
      refetchStandards();
    }
  }, [stateData?.state, refetchStandards]);

  // When the activated path changes, set the selected standard
  useEffect(() => {
    if (stateData?.activatedPath && courseData) {
      const path = stateData.activatedPath.split(".");
      const selectedStandard = get(courseData, path);
      if (selectedStandard) {
        setSelectedStandard(selectedStandard);
      }

      return;
    }

    // If we can't find the standard, or no activated path is set, then the entire standard is selected
    setSelectedStandard(undefined);
  }, [stateData?.activatedPath, courseData, setSelectedStandard]);

  // Fetch standard data when course is selected
  useEffect(() => {
    if (stateData?.selectedCourse) {
      refetchCourseData();
    }
  }, [stateData?.selectedCourse]);

  // Merge the standards and AP standards into selectCourseOptions
  useEffect(() => {
    const options: Array<{ key: string; val: string }> = [];
    if (standards && standards.length) {
      const basicOptions = standards.map(
        (option: { name: string; _id: string }) => ({
          key: option._id,
          val: option.name,
        })
      );
      options.push(...basicOptions);
    }
    if (apStandards && apStandards.length) {
      const apOptions = apStandards.map(
        (option: { name: string; _id: string }) => ({
          key: option._id,
          val: option.name,
        })
      );
      options.push(...apOptions);
    }
    if (options.length > 0) {
      setSelectCourseOptions(options);
    }

    // If no course is selected, set the first option as the default
    if (!stateData?.selectedCourse && options.length > 0) {
      setStateData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          selectedCourse: options[0].key,
        };
      });
    }
  }, [
    standards,
    apStandards,
    stateData?.selectedCourse,
    setSelectCourseOptions,
    setStateData,
  ]);

  if (!stateData || teacherDataLoading || sectionDataLoading) {
    return <LoadingSkeloton />;
  }

  const { selectedInfo, selectedCount } = getCountAndSelected(
    stateData,
    schoolsWithIntegral,
    teacherData,
    sectionData
  );

  return (
    <>
      {params.root === "select-scope" && (
        <>
          <div className="border border-b-2 bg-white">
            <div className="p-6">
              <h2 className="mb-4 font-serif text-2xl font-bold leading-10 text-dm-charcoal-800">
                Student Performance Reports
              </h2>
              <p className="font-serif text-lg font-bold leading-relaxed text-dm-charcoal-500">
                Select Data Scope
              </p>
            </div>
            <nav
              className={
                "-mb-px flex justify-between px-6 sm:justify-normal sm:space-x-14"
              }
              aria-label="Tabs"
            >
              {scopeTabs.map((tab) => (
                <NavLink
                  key={tab}
                  to={`${REACT_APP_ADMIN_LINK}/reports/student-performance/select-scope/${slugify(
                    tab,
                    {
                      lower: true,
                    }
                  )}`}
                  onClick={() =>
                    setStateData({
                      ...stateData,
                      scope: tab,
                    })
                  }
                  replace
                  className={clsx(
                    slugify(tab, { lower: true }) === params.page
                      ? "border-dm-brand-blue-500 font-bold"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",

                    "border-b-4 px-1 pb-4 text-center text-sm"
                  )}
                  aria-current={
                    slugify(tab, { lower: true }) === params.page
                      ? "page"
                      : undefined
                  }
                >
                  <span className="px-2">{tab}</span>
                </NavLink>
              ))}
            </nav>
          </div>
          <div id="scope-body" className="p-6">
            <div className="flex justify-between">
              <DeltaMathSelect
                options={yearOptions}
                label=""
                onChangeFn={(value) => {
                  const selectedOption =
                    yearOptions.find((opt) => opt.key === value) ||
                    yearOptions[0];

                  setStateData({
                    ...stateData,
                    startDate: selectedOption.startDate,
                    endDate: selectedOption.endDate,
                    selectedYear: selectedOption.key,
                  });
                }}
                value={stateData.selectedYear}
              />

              <Link
                to={`${REACT_APP_ADMIN_LINK}/reports/student-performance/select-standard`}
                replace
                className="justify-center rounded-md border border-transparent bg-dm-brand-blue-500 px-8 py-2 text-sm font-bold text-white shadow-sm hover:bg-dm-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-dm-brand-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Next: Select Standard
              </Link>
            </div>

            <div id="select-data-scope" className="mt-8">
              <ReportDataScope
                schoolsWithIntegral={schoolsWithIntegral}
                stateData={stateData}
                setStateData={setStateData}
                teacherData={teacherData}
                sectionData={sectionData}
              />
            </div>
          </div>
        </>
      )}
      {params.root === "select-standard" && (
        <>
          <div id="report-header" className="border border-b-2 bg-white px-6">
            <Link
              to={`${REACT_APP_ADMIN_LINK}/reports/student-performance/select-scope/${toLower(
                stateData?.scope
              )}`}
              className="align-center inline-flex py-4 font-bold"
            >
              <BackArrowIcon classes="mt-1.5" />
              <span className="pl-2 text-dm-brand-blue-500">
                Back to Data Scope
              </span>
            </Link>
            <h2 className="mb-4 font-serif text-2xl font-bold leading-10 text-neutral-900">
              Standards
            </h2>
            <h4 className="text-md font-sans text-base font-bold leading-relaxed text-neutral-900">
              Select the Standard
            </h4>
            <div className="flex flex-row justify-between pb-4">
              <ProgressFormattedCurrentSelection
                selectedInfo={selectedInfo}
                stateData={stateData}
              />
              <div>
                <button
                  onClick={() => {
                    navigate(
                      `${REACT_APP_ADMIN_LINK}/reports/student-performance/view-report`
                    );
                  }}
                  disabled={!selectedStandard}
                  className="justify-center rounded-md border border-transparent bg-dm-brand-blue-500 px-8 py-2 text-sm font-bold text-white shadow-sm hover:bg-dm-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-dm-brand-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Generate Report
                </button>
              </div>
            </div>
          </div>
          {standards && selectCourseOptions && (
            <div className="flex flex-row p-6">
              <div className="sm:w-full lg:w-72">
                <DeltaMathSelect
                  label="Select Course"
                  options={selectCourseOptions}
                  onChangeFn={(course) => {
                    setStateData({
                      ...stateData,
                      selectedCourse: course,
                      activatedPath: "",
                    });
                  }}
                  value={stateData.selectedCourse}
                />
              </div>
              {courseData && (
                <button
                  onClick={() => {
                    navigate(
                      `${REACT_APP_ADMIN_LINK}/reports/student-performance/view-global-report`,
                      {
                        replace: true,
                      }
                    );
                  }}
                  disabled={!courseData}
                  className="ml-2 mt-6 rounded-md border border-transparent bg-dm-brand-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  View All Standards for this Course
                </button>
              )}
            </div>
          )}
          <Transition
            as="div"
            show={true}
            appear={true}
            enter="transition-opacity duration-300"
            enterFrom="transform opacity-0"
            enterTo="transform opacity-100"
            leave="transition-opacity duration-300"
            leaveFrom="transform opacity-100"
            leaveTo="transform opacity-0"
          >
            {stateData.selectedCourse &&
              stateData?.state &&
              courseData &&
              Array.isArray(courseData.order) &&
              courseData?.order?.map(
                (item: string) =>
                  courseData?.data &&
                  courseData?.data[item] && (
                    <ProgressSelectStandard
                      key={item}
                      pathId={item}
                      stateData={stateData}
                      setStateData={setStateData}
                      standard={courseData.data[item]}
                      depth={1}
                      path={"data."}
                    />
                  )
              )}
          </Transition>
        </>
      )}
      {params.root === "view-report" && selectCourseOptions && progressData && (
        <ProgressReporWrapper
          stateData={stateData}
          setStateData={setStateData}
          data={progressData}
          isLoading={progressDataLoading}
          error={progressDataError}
          selectedInfo={selectedInfo}
        >
          <DisplayPerformanceGraphs
            progressData={progressData}
            stateData={stateData}
            setStateData={setStateData}
            schoolData={schoolData}
            sectionData={sectionData}
            teacherData={teacherData}
            standard={`${
              selectCourseOptions.find(
                (option) => option.key === stateData.selectedCourse
              )?.val
            } - ${(selectedStandard as SubStandard)?.label}`}
            FormattedSelection={() => (
              <ProgressFormattedCurrentSelection
                selectedInfo={selectedInfo}
                stateData={stateData}
              />
            )}
            isGlobal={false}
            standardDescription={(selectedStandard as SubStandard)?.description}
            rowsSelectedCount={selectedCount}
          />
        </ProgressReporWrapper>
      )}
      {params.root === "view-global-report" &&
        courseData &&
        stateData.startDate &&
        stateData.endDate && (
          <ProgressReporWrapper
            stateData={stateData}
            setStateData={setStateData}
            data={progressByStandardsData}
            isLoading={progressByStandardsLoading}
            error={progressByStandardsError}
            selectedInfo={selectedInfo}
          >
            <DisplayGlobalPerformanceGraph
              selectedCourse={courseData}
              stateData={stateData}
              setStateData={setStateData}
              progressByStandardData={progressByStandardsData}
              progressData={progressData}
              progressDataLoading={progressDataLoading}
              skillCodeToStandardMap={
                progressByStandardRequestParams.skillCodesToStandards
              }
              schoolData={schoolData}
              sectionData={sectionData}
              teacherData={teacherData}
              rowsSelectedCount={selectedCount}
            />
          </ProgressReporWrapper>
        )}
    </>
  );
};

export default StudentPerformanceContainer;

/**
 * Creates a reversed mapping of skill codes to their associated standard labels.
 * @param standard The standard object containing the skill codes and their associated descriptions.
 * @returns A mapping of each skill code to the standard lables which are associated with it.
 */
const skillCodeToStandardMap = (standard: Standard | SubStandard) => {
  function getSkillcodeMap(
    obj: SubStandard,
    map: Record<string, string[]> = {}
  ): Record<string, string[]> {
    if (obj.module_data) {
      for (const sk of obj.module_data) {
        const match = map[sk];

        if (match)
          map[sk] = Array.from(
            new Set([...match, obj.label || obj.description])
          );
        else map[sk] = [obj.label || obj.description];
      }
      return map;
    } else {
      for (const k in obj.data) {
        map = getSkillcodeMap(obj.data[k], map);
      }
    }
    return map;
  }

  let skToStandardMap: Record<string, string[]> = {};
  for (const key in standard.data) {
    skToStandardMap = getSkillcodeMap(standard.data[key], skToStandardMap);
  }
  return skToStandardMap;
};

/**
 * A helper function to attach the filters for fetching assignments based on the assignment type.
 * @warning This function mutates the params object.
 * @param stateData The current state data from the local storage, which includes the assignment type.
 * @param params The request parameters that may include additional filters.
 * @returns The filters for fetching assignments based on the assignment type.
 */
function attachAssignmentFilters(
  stateData: ProgressLocalStorageInterface | null,
  params: Omit<ProgressByStandardRequestInterface, "skillCodesToStandards">
): void {
  if (stateData?.assignmentType === "tests") {
    params.fetchTest = true;
    params.fetchPushed = false;
    params.fetchStandard = false;
  } else if (stateData?.assignmentType === "pushed-assignments") {
    params.fetchTest = false;
    params.fetchPushed = true;
    params.fetchStandard = false;
  }
}

/**
 * Builds the request parameters for the /progressByStandard endpoint.
 */
function buildProgressByStandardRequestParams(
  stateData: ProgressLocalStorageInterface | null,
  courseData: Standard | undefined,
  schoolsWithIntegral: SchoolsWithIntegral[],
  teacherData: TeacherData[],
  sectionData: SectionData[]
): ProgressByStandardRequestInterface | null {
  if (!courseData) return null;

  const params = buildRequestParams<ProgressByStandardRequestInterface>(
    stateData,
    schoolsWithIntegral,
    teacherData,
    sectionData
  );

  if (params) {
    params.skillCodesToStandards = skillCodeToStandardMap(courseData);
    attachAssignmentFilters(stateData, params);
  }

  return params;
}

/**
 * Builds the request parameters for the /progress endpoint.
 */
function buildProgressRequestParams(
  stateData: ProgressLocalStorageInterface | null,
  schoolsWithIntegral: SchoolsWithIntegral[],
  teacherData: TeacherData[],
  sectionData: SectionData[]
): ProgressRequestInterface | null {
  if (!stateData) return null;
  if (!stateData.skillcodes || stateData.skillcodes.length === 0) {
    return null;
  }

  const params = buildRequestParams<ProgressRequestInterface>(
    stateData,
    schoolsWithIntegral,
    teacherData,
    sectionData,
    {
      seriesLimit: ProgressSeriesLimit,
    }
  );

  if (params) {
    params.skillcodes = stateData.skillcodes || [];
    attachAssignmentFilters(stateData, params);
  }

  return params;
}
